enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error'
}

interface LogEntry {
    timestamp: string;
    level: LogLevel;
    message: string;
    context?: unknown;
    userAgent: string;
    url: string;
}

/**
 * Structured logging utility
 * Provides consistent logging interface with contextual information
 */
class Logger {
    private isDevelopment = import.meta.env.DEV;

    private formatLogEntry(level: LogLevel, message: string, context?: unknown): LogEntry {
        return {
            timestamp: new Date().toISOString(),
            level,
            message,
            context,
            userAgent: navigator.userAgent,
            url: window.location.href
        };
    }

    private log(level: LogLevel, message: string, context?: unknown): void {
        const logEntry = this.formatLogEntry(level, message, context);

        // Console output (always in development, errors only in production)
        if (this.isDevelopment || level === LogLevel.ERROR) {
            const consoleMethod = console[level] || console.log;
            consoleMethod('[Mavililer]', logEntry);
        }

        // Send to external service in production (if configured)
        if (!this.isDevelopment && level === LogLevel.ERROR) {
            this.sendToExternalService(logEntry);
        }
    }

    private sendToExternalService(logEntry: LogEntry): void {
        // Placeholder for external logging service integration
        // Example: Sentry, LogRocket, DataDog, etc.
        // This prevents unhandled errors in production
        try {
            // Example: Sentry.captureException(logEntry.message, { level: logEntry.level });
            // For now, we just prevent the unused variable warning
            void logEntry;
        } catch (error) {
            // Silently fail - don't break app if logging fails
            console.error('Failed to send log to external service:', error);
        }
    }

    public debug(message: string, context?: unknown): void {
        this.log(LogLevel.DEBUG, message, context);
    }

    public info(message: string, context?: unknown): void {
        this.log(LogLevel.INFO, message, context);
    }

    public warn(message: string, context?: unknown): void {
        this.log(LogLevel.WARN, message, context);
    }

    public error(message: string, context?: unknown): void {
        this.log(LogLevel.ERROR, message, context);
    }
}

// Export singleton instance
export const logger = new Logger();
export { LogLevel };
